import { db } from "../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  where,
  query,limit,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";

export const productService = {
  getByCategory: async (categoryName) => {
    const q = query(collection(db, "products"), where("category", "==", categoryName));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  getAll: async () => {
    const snapshot = await getDocs(collection(db, "products"));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  getOneByCategory: async (categoryValue) => {
    if (!categoryValue) throw new Error("Catégorie manquante.");

    const q = query(
      collection(db, "products"),
      where("category", "==", categoryValue),
      limit(1)
    );

    const snap = await getDocs(q);
    if (snap.empty) return null;

    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  },

  create: async (formData) => {
    const {
      name,
      description,
      sous_description,
      category,
      freeShipping,
      variantType,
      variants,
      price,

      images,
      mainImage,
    } = formData;

    // ── Validation ──────────────────────────────────────
    if (!name?.trim()) throw new Error("Le nom est requis.");
    if (!category) throw new Error("La catégorie est requise.");
        if (!freeShipping) throw new Error("La freeShipping est requise.");

    if (!description?.trim()) throw new Error("La description est requise.");

    if (!Array.isArray(images) || images.length === 0)
      throw new Error("Veuillez uploader au moins 1 image.");
    if (!mainImage) throw new Error("Image principale manquante.");

    if (!Array.isArray(variants) || variants.length === 0)
      throw new Error("Ajoutez au moins une variante avec un prix.");

    const cleanVariants = variants.map((v, i) => ({
      id: v.id || `v${i + 1}`,
      label: String(v.label || "").trim(),
      price: parseFloat(v.price),
      stock: parseInt(v.stock, 10) || 0,
    }));

    if (cleanVariants.some((v) => !v.label || !Number.isFinite(v.price) || v.price <= 0))
      throw new Error("Chaque variante doit avoir un libellé et un prix valide.");

    // Prix "à partir de" = plus petit prix des variantes
    const basePrice = Math.min(...cleanVariants.map((v) => v.price));

    // ── Enregistrement ──────────────────────────────────
    try {
      const docRef = await addDoc(collection(db, "products"), {
        name: name.trim(),
        description: description.trim(),
        sous_description: sous_description?.trim() || "",
        category: category.trim(),
        freeShipping: freeShipping.trim(),

        variantType: variantType || "",
        variants: cleanVariants,
        price: basePrice,
        images,
        mainImage,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      return { id: docRef.id };
    } catch (error) {
      console.error("Erreur Firestore:", error);
      throw new Error(`Erreur création produit: ${error.message}`);
    }
  },

  delete: async (id) => {
    if (!id) throw new Error("ID produit manquant.");
    try {
      await deleteDoc(doc(db, "products", id));
    } catch (error) {
      throw new Error(`Erreur suppression: ${error.message}`);
    }
  },

  update: async (id, updates) => {
    if (!id) throw new Error("ID produit manquant.");

    const payload = { ...updates, updatedAt: new Date() };

    if (Array.isArray(updates.variants)) {
      const clean = updates.variants.map((v, i) => ({
        id: v.id || `v${i + 1}`,
        label: String(v.label || "").trim(),
        price: parseFloat(v.price),
        stock: parseInt(v.stock, 10) || 0,
      }));

      if (clean.some((v) => !v.label || !Number.isFinite(v.price) || v.price <= 0)) {
        throw new Error("Chaque variante doit avoir un libellé et un prix valide.");
      }

      payload.variants = clean;
      payload.price = Math.min(...clean.map((v) => v.price));
    }

    try {
      await updateDoc(doc(db, "products", id), payload);
    } catch (error) {
      throw new Error(`Erreur mise à jour: ${error.message}`);
    }
  },

  getReviews: async (productId) => {
    if (!productId) throw new Error("ID produit manquant.");
    const q = query(
      collection(db, "products", productId, "reviews"),
      orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  addReview: async (productId, { userId, rating, comment }) => {
    const note = Number(rating);
    const text = comment?.trim();

    if (!productId || !userId) throw new Error("Connexion requise.");
    if (!Number.isInteger(note) || note < 1 || note > 5)
      throw new Error("La note doit être entre 1 et 5.");
    if (!text) throw new Error("Le commentaire est requis.");

    const ref = await addDoc(collection(db, "products", productId, "reviews"), {
      userId,
      rating: note,
      comment: text,
      createdAt: serverTimestamp(),
    });

    return { id: ref.id };
  },
};